import React, {
  FC,
  ReactNode,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
} from 'react';
import cn from 'classnames';
import { useSpring, animated } from 'react-spring';
import { createPortal } from 'react-dom';
import { useClickOutside } from 'utils/useClickOutside';
import css from './ModalOverlay.module.scss';

type ModalAnimProps = {
  isWide?: boolean;
  children: ReactNode;
};

const ModalAnim = forwardRef<HTMLDivElement, ModalAnimProps>(
  ({ children, isWide }, ref) => {
    useEffect(() => {
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.removeAttribute('style');
      };
    }, []);

    const styles = useSpring({
      from: { transform: 'translateY(-40px)', opacity: 0 },
      to: { transform: 'translateY(0)', opacity: 1 },
      config: {
        mass: 1,
        tension: 300,
        friction: 30,
      },
    });

    return (
      <div className={cn(css.content, 'ModalOverlay_content')}>
        <animated.div className={css.bg} />
        <animated.div
          className={cn(css.dialog, 'ModalOverlay_dialog', {
            [css.isWide]: isWide,
          })}
          style={styles}
        >
          <div className={css.children} ref={ref}>
            {children}
          </div>
        </animated.div>
      </div>
    );
  },
);

ModalAnim.displayName = 'ModalAnim';

type ModalOverlayProps = {
  isOpen?: boolean;
  onCloseModal: () => void;
  children: ReactNode;
  className?: string;
  isWide?: boolean;
  /** Разрешает ли закрывать при клике вне модалки */
  clickOutside?: boolean;
};

const ModalOverlay: FC<ModalOverlayProps> = ({
  isOpen = false,
  children,
  onCloseModal,
  className,
  isWide,
  clickOutside = false,
}) => {
  const modalRef = useRef<HTMLDivElement | null>(null);
  const accessClickOutside = clickOutside && isOpen;
  useClickOutside(modalRef, onCloseModal, accessClickOutside);

  const onEsc = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseModal();
    },
    [onCloseModal],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onEsc]);

  if (!isOpen) return null;

  const portalRoot = document.getElementById('modal-portal') ?? document.body;

  return createPortal(
    <div className={cn(css.modal, className, 'ModalOverlay')} data-filter-interactive>
      <ModalAnim ref={modalRef} isWide={isWide}>
        {children}
      </ModalAnim>
    </div>,
    portalRoot,
  );
};

export default ModalOverlay;
